import { useEffect, useMemo, useRef, useState } from 'react'
import { MAX_FLOWS_TO_DOCUMENT } from '~/constants/commandLimits'
import { UsedCommandType } from '~/constants/commands'
import { AgentRequest, CommandType } from '~/protos/agents'
import { ProjectSelectionOption } from '~/store/taskFormSlice'
import { SimpleMultiSelect } from './SimpleMultiSelect'
import { TaskParams } from '~/components/task-forms/BaseTaskForm'

interface FlowNameSelectorProps {
  currentTask: TaskParams | null
  setCommandString: (commandString: string) => void
  currentOption?: ProjectSelectionOption
  withExamplePrefilled?: boolean
  command: UsedCommandType
  onFlowNamesChange: (flowNamesString: string | null) => void
  isMultiSelect?: boolean
  initialSelectedFlows?: string[]
  flowNames: string[] | null
  isLoadingFlowNames: boolean
  /** Optional cap on the number of flows that can be selected. When omitted,
   *  falls back to the per-command default (see `getMaxFlowsForCommand`).
   *  `0` or negative values are treated as "no cap". */
  maxFlows?: number
}

const isMultiSelectEnabled = (command: UsedCommandType): boolean => {
  switch (command) {
    case CommandType.MUNIT_TEST_CREATION:
      return false
    case CommandType.GENERATE_DOCUMENTATION:
    case CommandType.GENERATE_IMAGE:
      return true
    default:
      return false
  }
}

/** Document generation degrades in quality (and gets very slow) past ~4 flows
 *  per request — surface a hard cap so users get an explicit error instead of
 *  silently kicking off a 10-flow run that returns mush.
 *
 *  Returning `undefined` means "no cap"; callers that want a different cap
 *  (e.g. tighter limit for a custom workflow) can override via the
 *  `maxFlows` prop on `FlowNameSelector`. */
const getMaxFlowsForCommand = (command: UsedCommandType): number | undefined => {
  switch (command) {
    case CommandType.GENERATE_DOCUMENTATION:
      return MAX_FLOWS_TO_DOCUMENT
    default:
      return undefined
  }
}

/** User-facing message rendered under the select when the user attempts to
 *  select more than `cap` flows. Phrased generically so the same string works
 *  for any future cap > 4. */
const buildMaxFlowsMessage = (cap: number): string =>
  `You can select up to ${cap} ${cap === 1 ? 'flow' : 'flows'} for this command.`

/** When loading an existing task (or syncing `initialSelectedFlows` from the parent),
 *  ensure we never start with more than `maxFlows` selected — the interactive handler
 *  already blocks going past the cap, but this path must not bypass it. */
const clampInitialFlowsToMax = (
  flows: string[],
  max: number | undefined
): { clamped: string[]; wasTruncated: boolean } => {
  if (max === undefined || flows.length <= max) {
    return { clamped: flows, wasTruncated: false }
  }
  return { clamped: flows.slice(0, max), wasTruncated: true }
}

const generateInitialFlowNames = (command: UsedCommandType): string[] => {
  switch (command) {
    case CommandType.MUNIT_TEST_CREATION:
    case CommandType.GENERATE_DOCUMENTATION:
    case CommandType.GENERATE_IMAGE:
      return ['get:\\members\\(memberId):loyalty-management-api-config']
    default:
      return []
  }
}

function FlowNameSelector({
  currentTask = { request: AgentRequest.fromPartial({}) },
  setCommandString,
  currentOption = 'codeContext',
  withExamplePrefilled = false,
  command,
  onFlowNamesChange,
  isMultiSelect: isMultiSelectProp,
  initialSelectedFlows,
  flowNames,
  isLoadingFlowNames,
  maxFlows: maxFlowsProp
}: FlowNameSelectorProps) {
  // Memoize exampleFlowNames to prevent new array reference on every render
  const exampleFlowNames = useMemo(() => generateInitialFlowNames(command), [command])
  const exampleFlowNamesString = useMemo(() => exampleFlowNames.join(', '), [exampleFlowNames])
  const isMultiSelect =
    isMultiSelectProp !== undefined ? isMultiSelectProp : isMultiSelectEnabled(command)
  // Resolve the effective cap once: explicit prop wins, otherwise fall back to
  // the per-command default. Treat `<= 0` as "no cap" so callers can opt out.
  const maxFlows = useMemo<number | undefined>(() => {
    const resolved = maxFlowsProp !== undefined ? maxFlowsProp : getMaxFlowsForCommand(command)
    if (resolved === undefined || resolved <= 0) return undefined
    return resolved
  }, [maxFlowsProp, command])

  const [selectedKeys, setSelectedKeys] = useState<string[]>(() => {
    const base: string[] =
      initialSelectedFlows && initialSelectedFlows.length > 0
        ? initialSelectedFlows
        : withExamplePrefilled
          ? exampleFlowNames
          : []
    return clampInitialFlowsToMax(base, maxFlows).clamped
  })
  // Inline error message rendered under the select. Set when the user attempts
  // to select more than `maxFlows`, or when initial/synced `initialSelectedFlows`
  // had to be clamped. Cleared on the next selection change that stays
  // at-or-under the cap, or when synced props fit. Kept null (vs empty string)
  // so the error <p /> is omitted from the DOM when there's nothing to say.
  const [maxFlowsError, setMaxFlowsError] = useState<string | null>(() => {
    const base: string[] =
      initialSelectedFlows && initialSelectedFlows.length > 0
        ? initialSelectedFlows
        : withExamplePrefilled
          ? exampleFlowNames
          : []
    return clampInitialFlowsToMax(base, maxFlows).wasTruncated && maxFlows !== undefined
      ? buildMaxFlowsMessage(maxFlows)
      : null
  })

  // Use refs for callbacks to avoid infinite loops when parent doesn't memoize them
  const onFlowNamesChangeRef = useRef(onFlowNamesChange)
  const setCommandStringRef = useRef(setCommandString)
  useEffect(() => {
    onFlowNamesChangeRef.current = onFlowNamesChange
    setCommandStringRef.current = setCommandString
  })

  // Track previous values to detect actual changes
  const prevWithExamplePrefilledRef = useRef(withExamplePrefilled)
  const prevInitialSelectedFlowsRef = useRef(initialSelectedFlows)

  // Sync selected keys when withExamplePrefilled changes (e.g., user toggles "Try Example")
  useEffect(() => {
    // Only run when withExamplePrefilled actually changes to true
    if (withExamplePrefilled && !prevWithExamplePrefilledRef.current) {
      setSelectedKeys(exampleFlowNames)
    }
    prevWithExamplePrefilledRef.current = withExamplePrefilled
  }, [withExamplePrefilled, exampleFlowNames])

  // Update selected flows when initialSelectedFlows changes (for editing mode)
  useEffect(() => {
    if (!initialSelectedFlows || initialSelectedFlows.length === 0) return

    // Compare with previous value to detect actual changes
    const prevFlows = prevInitialSelectedFlowsRef.current
    const prevFlowsSet = new Set(prevFlows ?? [])
    const newFlowsSet = new Set(initialSelectedFlows)

    const hasActualChange =
      prevFlowsSet.size !== newFlowsSet.size ||
      [...newFlowsSet].some(flow => !prevFlowsSet.has(flow))

    if (hasActualChange) {
      const { clamped, wasTruncated } = clampInitialFlowsToMax(initialSelectedFlows, maxFlows)
      setSelectedKeys(clamped)
      setMaxFlowsError(
        wasTruncated && maxFlows !== undefined ? buildMaxFlowsMessage(maxFlows) : null
      )
    }
    prevInitialSelectedFlowsRef.current = initialSelectedFlows
  }, [initialSelectedFlows, maxFlows])

  // Re-clamp when `maxFlows` tightens below the current selection (e.g. the
  // parent flips `command` from `GENERATE_IMAGE` (no cap) to
  // `GENERATE_DOCUMENTATION` (cap=4) without remounting the component, while
  // 5 flows are already selected). The `initialSelectedFlows` sync above
  // bails when its dependency hasn't changed, so we need a separate effect
  // dedicated to cap-only changes — otherwise the user sees an out-of-cap
  // selection with no error and submit fails downstream with a backend 400.
  // When `maxFlows` transitions back to "no cap", also drop any stale error
  // left over from a prior capped state.
  useEffect(() => {
    if (maxFlows === undefined) {
      setMaxFlowsError(null)
      return
    }
    setSelectedKeys(prev => {
      if (prev.length <= maxFlows) return prev
      setMaxFlowsError(buildMaxFlowsMessage(maxFlows))
      return prev.slice(0, maxFlows)
    })
  }, [maxFlows])

  const flowNamesString: string = selectedKeys.join(', ')

  // Use flow names from parent (provided by unified repo metadata call)

  // Enforce single selection mode - use functional update to avoid dependency on selectedKeys
  useEffect(() => {
    if (!isMultiSelect) {
      setSelectedKeys(prev => (prev.length > 1 ? [prev[0]] : prev))
    }
  }, [isMultiSelect])

  // Auto-select only when exactly 1 flow is returned from repo-metadata
  // For multiple flows, let user manually select to avoid unintended processing
  useEffect(() => {
    // Skip if in example mode (has its own flows) or editing mode
    if (withExamplePrefilled || initialSelectedFlows) return
    // Skip if no flows loaded
    if (!flowNames || flowNames.length === 0) return

    // Only auto-select if exactly 1 flow - use functional update to check current state
    if (flowNames.length === 1) {
      setSelectedKeys(prev => (prev.length > 0 ? prev : [flowNames[0]]))
    }
    // For multiple flows, don't auto-select - user must choose
  }, [flowNames, withExamplePrefilled, initialSelectedFlows])

  // Track last synced command string to prevent loops
  const lastSyncedCommandStringRef = useRef<string | null>(null)

  // Update command string whenever selectedKeys changes
  useEffect(() => {
    // Skip if no flows selected and not in example mode
    if (!flowNamesString && !withExamplePrefilled) return
    // Skip if we already synced this value
    if (lastSyncedCommandStringRef.current === flowNamesString) return

    lastSyncedCommandStringRef.current = flowNamesString
    setCommandStringRef.current(flowNamesString)
  }, [flowNamesString, withExamplePrefilled])

  // Use loading state and data from parent
  const isLoading = isLoadingFlowNames
  const hasFlowNames = flowNames && flowNames.length > 0

  // Track last notified flow names to prevent loops
  const lastNotifiedFlowNamesRef = useRef<string | null>(null)

  useEffect(() => {
    const result = flowNamesString === '' ? null : flowNamesString
    // Only notify if value actually changed
    if (lastNotifiedFlowNamesRef.current !== result) {
      lastNotifiedFlowNamesRef.current = result
      onFlowNamesChangeRef.current(result)
    }
  }, [flowNamesString])

  const placeholderMessages = useMemo(
    () => ({
      initial: {
        none: '',
        codeContext: isMultiSelect
          ? 'Select repository details to view flows'
          : 'Select repository details to view flows',
        upload: isMultiSelect ? 'Upload a repo to view flows' : 'Upload a repo to view flows',
        mappingTable: isMultiSelect
          ? 'Select repository details to view flows'
          : 'Select repository details to view flows'
      },
      loading: 'Loading flow names...',
      done: isMultiSelect ? 'Select one or more flows' : 'Select a flow',
      noFlows: 'No flows found'
    }),
    [isMultiSelect]
  )

  const getPlaceholder = useMemo(() => {
    if (isLoading) return placeholderMessages.loading
    else if (hasFlowNames || (initialSelectedFlows && initialSelectedFlows.length > 0))
      return placeholderMessages.done
    else if (!flowNames) return placeholderMessages.initial[currentOption]
    return placeholderMessages.noFlows
  }, [isLoading, placeholderMessages, currentOption, hasFlowNames, initialSelectedFlows, flowNames])

  const handleSelectionChange = (newSelection: string[]) => {
    // If not multi-select, ensure only one item is selected
    if (!isMultiSelect && newSelection.length > 1) {
      setSelectedKeys([newSelection[0]])
      setMaxFlowsError(null)
      return
    }

    // Multi-select with a per-command cap (e.g. doc generation = 4): reject the
    // attempted addition rather than silently truncating, so the user gets
    // explicit feedback. Selection stays at whatever it was before they tried
    // to push past the cap. Reductions (which can never exceed the cap) always
    // go through and clear any prior error.
    if (maxFlows !== undefined && newSelection.length > maxFlows) {
      setMaxFlowsError(buildMaxFlowsMessage(maxFlows))
      return
    }

    setSelectedKeys(newSelection)
    if (maxFlowsError !== null) setMaxFlowsError(null)
  }

  // Combine flowNames with initialSelectedFlows to ensure we show flows even in upload mode when editing
  const availableFlows = useMemo(() => {
    if (withExamplePrefilled) return exampleFlowNames

    const repositoryFlows = flowNames ?? []
    const taskFlows = initialSelectedFlows ?? []
    const combined = [...new Set([...repositoryFlows, ...taskFlows])].filter(Boolean)

    return combined
  }, [withExamplePrefilled, exampleFlowNames, flowNames, initialSelectedFlows])

  const items = useMemo(() => {
    return availableFlows.map(flowName => ({
      value: flowName,
      label: flowName
    }))
  }, [availableFlows])

  const generateLabel = () => {
    return isMultiSelect ? 'Flow Name(s)' : 'Flow Name'
  }

  return (
    <div className='w-full' data-testid='flow-name-selector'>
      {withExamplePrefilled ? (
        <SimpleMultiSelect
          label={generateLabel()}
          placeholder={exampleFlowNamesString}
          selectedKeys={selectedKeys}
          onSelectionChange={() => {}}
          readOnly={true}
          disabled={false}
          items={items}
          isMultiSelect={isMultiSelect}
          size='sm'
        />
      ) : (
        <SimpleMultiSelect
          label={generateLabel()}
          placeholder={getPlaceholder}
          selectedKeys={selectedKeys}
          onSelectionChange={handleSelectionChange}
          disabled={isLoading}
          items={items}
          isMultiSelect={isMultiSelect}
          size='sm'
        />
      )}
      {maxFlowsError !== null && (
        <p
          className='text-tokens-status-error-text text-xs mt-1'
          role='alert'
          data-testid='flow-name-selector-max-error'
        >
          {maxFlowsError}
        </p>
      )}
    </div>
  )
}

export default FlowNameSelector
